/**
 * 제어밸브 완전개방(고장) 시 실제 통과유량 — Cv 기준 정식 계산.
 *
 * 근거: ANSI/ISA-75.01.01, IEC 60534-2-1 (Fisher Control Valve Handbook,
 * Emerson/Fisher, 4th/5th Ed. "Sizing Valves for Liquids" ·
 * "Sizing Valves for Compressible Fluids" 절의 식을 그대로 옮겼다).
 *
 * 밸브 사이징 절차(필요 Cv 를 구하는 것)를 **거꾸로** 쓴다 — 이미 설치된
 * 밸브의 Cv 를 알고, 완전개방 상태에서 주어진 압력차로 얼마나 흐르는지
 * 구하는 것이므로 사이징 식을 유량에 대해 그대로 푼 것뿐이다.
 *
 * 단순화: 배관 이음(리듀서 등)에 의한 Fp 보정은 하지 않는다(Fp=1 — 밸브가
 * 배관과 같은 크기로 설치된 경우와 같다). 실제로 리듀서가 있으면 근사가
 * 보수적이지 않을 수 있다.
 *
 * 단위: 압력 kPa(절대) · 온도 K · 밀도 kg/m³ · 유량 액체 kg/h, 기체 kg/h
 */

function invalid(message: string): { ok: false; reason: "invalid-input"; message: string } {
  return { ok: false, reason: "invalid-input", message };
}

function checkPositive(label: string, v: number): string | null {
  return !Number.isFinite(v) || v <= 0
    ? `${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`
    : null;
}

/* ── 액체 ──────────────────────────────────────────────────
 * w = N6·Fp·Cv·√(ΔP·ρ1)   [N6 = 2.73, w: kg/h, ΔP: kPa, ρ1: kg/m³]
 * 초크(공동현상/플래싱): ΔPmax = FL²·(P1 − FF·Pv), FF = 0.96 − 0.28·√(Pv/Pc) */

export const N6_LIQUID_MASS = 2.73;

export interface CvLiquidInput {
  readonly cv: number;
  readonly upstreamPressureKpaAbs: number;
  readonly downstreamPressureKpaAbs: number;
  readonly densityKgM3: number;
  /** 밸브의 정격 압력회복계수 FL. 자료 없으면 통상값 0.9(글로브 밸브 기준). */
  readonly fl?: number;
  /** 배출 조건 증기압 (kPa 절대). 초크(플래싱) 판정에 쓴다. 없으면 0. */
  readonly vaporPressureKpaAbs?: number;
  /** 유체의 열역학적 임계압력 (kPa 절대). 없으면 FF=0.96 (Pv=0 과 같음). */
  readonly criticalPressureKpaAbs?: number;
}

export interface CvLiquidSuccess {
  readonly ok: true;
  readonly massFlowKgH: number;
  readonly choked: boolean;
  readonly appliedPressureDropKpa: number;
  readonly warnings: readonly string[];
}
export interface CvFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}
export type CvLiquidResult = CvLiquidSuccess | CvFailure;

export function cvValveFlowLiquid(input: CvLiquidInput): CvLiquidResult {
  for (const [label, v] of [
    ["Cv", input.cv],
    ["밀도", input.densityKgM3],
  ] as const) {
    const err = checkPositive(label, v);
    if (err) return invalid(err);
  }
  const { upstreamPressureKpaAbs: p1, downstreamPressureKpaAbs: p2 } = input;
  if (!Number.isFinite(p1) || !Number.isFinite(p2)) {
    return invalid("압력이 올바르지 않습니다.");
  }
  if (p1 <= p2) {
    return invalid(
      "상류(감압 전) 압력이 하류(감압 후) 압력보다 커야 합니다. 두 값이 " +
        "절대압인지 확인하십시오.",
    );
  }
  const FL = input.fl ?? 0.9;
  if (!Number.isFinite(FL) || FL <= 0 || FL > 1) {
    return invalid("FL 은 0보다 크고 1 이하이어야 합니다.");
  }
  const Pv = input.vaporPressureKpaAbs ?? 0;
  const Pc = input.criticalPressureKpaAbs ?? Infinity;
  if (!Number.isFinite(Pv) || Pv < 0) return invalid("증기압이 올바르지 않습니다.");

  const FF = 0.96 - 0.28 * Math.sqrt(Math.max(Pv / Pc, 0));
  const dPmax = FL * FL * (p1 - FF * Pv);
  const dPactual = p1 - p2;
  const choked = dPmax < dPactual;
  const dPUsed = Math.max(Math.min(dPactual, dPmax), 0);

  const massFlowKgH =
    N6_LIQUID_MASS * input.cv * Math.sqrt(dPUsed * input.densityKgM3);

  const warnings: string[] = [
    "ANSI/ISA-75.01.01 · IEC 60534-2-1 의 밸브 Cv 유동식을 적용한 값입니다 " +
      "(밸브 사이징 절차를 유량에 대해 거꾸로 풀었습니다). Fp(배관 이음 " +
      "보정)는 1로 가정했습니다 — 리듀서 등이 있으면 실제 유량이 이보다 " +
      "낮을 수 있습니다.",
    `FL(정격 압력회복계수) ${FL} 을 적용했습니다. 제작사 자료값이 아니면 ` +
      "근거를 남기십시오(글로브 밸브 통상 0.8~0.9).",
  ];
  if (choked) {
    warnings.push(
      "초크(공동현상 또는 플래싱) 상태입니다 — 하류 압력이 더 낮아져도 " +
        "유량은 늘지 않습니다. 계산에는 ΔPmax 를 적용했습니다.",
    );
  }

  return { ok: true, massFlowKgH, choked, appliedPressureDropKpa: dPUsed, warnings };
}

/* ── 기체 ──────────────────────────────────────────────────
 * w = N8·Fp·Cv·P1·Y·√(x·M/(T1·Z))   [N8 = 0.948, w: kg/h, P1: kPa, T1: K]
 * Y = 1 − x/(3·Fk·xT), Fk = k/1.4, x = ΔP/P1
 * 초크: x ≥ Fk·xT 이면 x 를 Fk·xT 로, Y 를 0.667 로 고정. */

export const N8_GAS_MASS = 0.948;

export interface CvGasInput {
  readonly cv: number;
  readonly upstreamPressureKpaAbs: number;
  readonly downstreamPressureKpaAbs: number;
  readonly temperatureK: number;
  readonly molecularWeight: number;
  readonly specificHeatRatio: number;
  readonly compressibility?: number;
  /**
   * 밸브의 정격 압력강하비 계수 xT. 제작사 자료가 없으면 글로브 밸브
   * 통상값 0.7 을 쓴다. 회전형(볼·버터플라이) 밸브는 훨씬 작다(0.15~0.3).
   */
  readonly xT?: number;
}

export interface CvGasSuccess {
  readonly ok: true;
  readonly massFlowKgH: number;
  readonly choked: boolean;
  readonly expansionFactorY: number;
  readonly warnings: readonly string[];
}
export type CvGasResult = CvGasSuccess | CvFailure;

export function cvValveFlowGas(input: CvGasInput): CvGasResult {
  for (const [label, v] of [
    ["Cv", input.cv],
    ["온도", input.temperatureK],
    ["분자량", input.molecularWeight],
  ] as const) {
    const err = checkPositive(label, v);
    if (err) return invalid(err);
  }
  const { specificHeatRatio: k } = input;
  if (!Number.isFinite(k) || k <= 1) return invalid("비열비 k 는 1보다 커야 합니다.");
  const Z = input.compressibility ?? 1.0;
  if (!Number.isFinite(Z) || Z <= 0) return invalid("압축계수 Z 는 0보다 커야 합니다.");
  const xT = input.xT ?? 0.7;
  if (!Number.isFinite(xT) || xT <= 0 || xT > 1) {
    return invalid("xT 는 0보다 크고 1 이하이어야 합니다.");
  }
  const { upstreamPressureKpaAbs: p1, downstreamPressureKpaAbs: p2 } = input;
  if (!Number.isFinite(p1) || !Number.isFinite(p2) || p2 < 0) {
    return invalid("압력이 올바르지 않습니다.");
  }
  if (p1 <= p2) {
    return invalid(
      "상류(감압 전) 압력이 하류(감압 후) 압력보다 커야 합니다. 두 값이 " +
        "절대압인지 확인하십시오.",
    );
  }

  const Fk = k / 1.4;
  const xActual = (p1 - p2) / p1;
  const xLimit = Fk * xT;
  const choked = xActual >= xLimit;
  const x = choked ? xLimit : xActual;
  const Y = choked ? 2 / 3 : 1 - x / (3 * Fk * xT);

  const massFlowKgH =
    N8_GAS_MASS *
    input.cv *
    p1 *
    Y *
    Math.sqrt((x * input.molecularWeight) / (Z * input.temperatureK));

  const warnings: string[] = [
    "ANSI/ISA-75.01.01 · IEC 60534-2-1 의 밸브 Cv 유동식을 적용한 값입니다 " +
      "(밸브 사이징 절차를 유량에 대해 거꾸로 풀었습니다). Fp(배관 이음 " +
      "보정)는 1로 가정했습니다.",
    `xT(정격 압력강하비 계수) ${xT} 를 적용했습니다. 제작사 유량계수표 값이 ` +
      "아니면 근거를 남기십시오 — 밸브 형식·개도에 따라 크게 달라집니다 " +
      "(예: 글로브 밸브 약 0.7, 회전형 밸브 0.15~0.3).",
  ];
  if (choked) {
    warnings.push(
      "임계(초크) 유동입니다 — 하류 압력이 더 낮아져도 유량은 늘지 않습니다.",
    );
  }

  return { ok: true, massFlowKgH, choked, expansionFactorY: Y, warnings };
}
